import React from 'react'
import { Box, Group, Slider } from '@mantine/core'
import { AppIconButton, AppText } from '../app/common/ui'
import type EditorStore from '../app/stores/EditorStore'
import type { Textbox } from '../app/models/shootTextbox'
import { SHOOTS_FONT_SLIDER_REFERENCE_HEIGHT_PX } from '../app/utils/shootsCoords'

/**
 * Font size slider (Option A): labels stay 14–72 (step 2) as “logical px at reference-tall media,” not raw % of height.
 * Stored value: `Textbox.fontSize = sliderValue / SHOOTS_FONT_SLIDER_REFERENCE_HEIGHT_PX` (see `shootsCoords.ts`).
 * Opacity and color swatches map 1:1 to `EditorStore` (no unit conversion).
 */
const SWATCH_COLORS = [
  '#FFFFFF',
  '#000000',
  '#FF0000',
  '#FFFF00',
  '#0000FF',
  '#00FF00',
  '#FFA500',
  '#00FFFF',
  '#FF00FF'
] as const

function colorsEqual (a: string, b: string): boolean {
  return a.replace(/\s/g, '').toLowerCase() === b.replace(/\s/g, '').toLowerCase()
}

export interface TextboxStylingToolbarProps {
  selectedTextbox: Textbox | null;
  editorStore: EditorStore;
  onHide?: () => void;
}

/** Fixed bottom bar: font size (slider Option A), preset colors, opacity. Wired to EditorStore (MobX). */
export function TextboxStylingToolbar ({ selectedTextbox, editorStore }: TextboxStylingToolbarProps) {
  if (!selectedTextbox) return null

  /* Slider display: invert normalized fontSize → “reference px” for the 14–72 control. */
  let sliderFontPx = Math.round(selectedTextbox.fontSize * SHOOTS_FONT_SLIDER_REFERENCE_HEIGHT_PX)
  sliderFontPx = Math.min(72, Math.max(14, sliderFontPx))
  if (sliderFontPx % 2 !== 0) sliderFontPx -= 1
  const clampedSlider = Math.max(14, Math.min(72, sliderFontPx))

  return (
    <Box
      component="div"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 200,
        padding: '12px',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
        backgroundColor: '#1a1a1a',
        borderTop: '1px solid #333',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        alignItems: 'center',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.45)'
      }}
    >
      <Group spacing="sm" align="center" noWrap sx={{ flex: '1 1 200px', minWidth: 0 }}>
        <Slider
          label="Font Size"
          min={14}
          max={72}
          step={2}
          value={clampedSlider}
          onChange={(size) =>
            editorStore.updateFontSize(selectedTextbox.id, size / SHOOTS_FONT_SLIDER_REFERENCE_HEIGHT_PX)
          }
          marks={[
            { value: 14, label: '14' },
            { value: 72, label: '72' }
          ]}
          color="orange"
          styles={{
            root: { flex: 1, minWidth: 0 },
            markLabel: { color: '#aaa', fontSize: 10 },
            label: { color: '#e0e0e0' }
          }}
        />
        <AppText size="sm" sx={{ flexShrink: 0, width: 40, textAlign: 'right', color: '#ccc' }}>
          {clampedSlider}
        </AppText>
      </Group>

      <Group spacing="xs" sx={{ flex: '0 0 auto' }}>
        {SWATCH_COLORS.map((color) => (
          <AppIconButton
            key={color}
            size={32}
            radius="md"
            variant="default"
            aria-label={`Color ${color}`}
            style={{
              backgroundColor: color,
              border: colorsEqual(selectedTextbox.color, color) ? '3px solid #ffffff' : '1px solid #666',
              cursor: 'pointer'
            }}
            onClick={() => editorStore.updateColor(selectedTextbox.id, color)}
          />
        ))}
      </Group>

      <Group spacing="sm" align="center" noWrap sx={{ flex: '1 1 200px', minWidth: 0 }}>
        <Slider
          label="Opacity"
          min={0}
          max={100}
          step={5}
          value={selectedTextbox.opacity * 100}
          onChange={(opacity) => editorStore.updateOpacity(selectedTextbox.id, opacity / 100)}
          marks={[
            { value: 0, label: '0%' },
            { value: 100, label: '100%' }
          ]}
          color="orange"
          styles={{
            root: { flex: 1, minWidth: 0 },
            markLabel: { color: '#aaa', fontSize: 10 },
            label: { color: '#e0e0e0' }
          }}
        />
        <AppText size="sm" sx={{ flexShrink: 0, width: 44, textAlign: 'right', color: '#ccc' }}>
          {Math.round(selectedTextbox.opacity * 100)}%
        </AppText>
      </Group>
    </Box>
  )
}
